import { useEffect, useRef, useState } from "react";
import { Fingerprint } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  isBiometricAvailable,
  saveCredentials,
  getBiometricTypeLabel,
} from "@/lib/biometricAuth";
import { useToast } from "@/hooks/use-toast";

/**
 * Shows a biometric opt-in dialog after login, if pending credentials
 * were stored in sessionStorage by the Auth page.
 * This runs on the dashboard so navigation is never blocked.
 */
export function BiometricOptInPrompt() {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("Biometrics");
  const credsRef = useRef<{ email: string; password: string } | null>(null);
  const checkedRef = useRef(false);
  const { toast } = useToast();

  useEffect(() => {
    if (checkedRef.current) return;
    checkedRef.current = true;

    // Poll for the biometric prompt flag — it may be set just before or after navigation
    let attempts = 0;
    const maxAttempts = 10;
    const intervalMs = 500; // Check every 500ms for up to 5s

    const poll = setInterval(() => {
      attempts++;
      const pending = sessionStorage.getItem("tm_pending_biometric_prompt");
      console.log(`[BiometricOptIn] Poll #${attempts}, pending=${pending}`);

      if (pending !== "true") {
        if (attempts >= maxAttempts) {
          console.log("[BiometricOptIn] No biometric prompt flag found after polling");
          clearInterval(poll);
        }
        return;
      }

      // Found it — stop polling and process
      clearInterval(poll);
      sessionStorage.removeItem("tm_pending_biometric_prompt");

      const raw = sessionStorage.getItem("tm_pending_biometric_creds");
      console.log("[BiometricOptIn] Has creds:", !!raw);
      if (!raw) return;
      sessionStorage.removeItem("tm_pending_biometric_creds");

      let creds: { email: string; password: string };
      try {
        creds = JSON.parse(raw);
      } catch {
        return;
      }

      (async () => {
        try {
          console.log("[BiometricOptIn] Checking biometric availability...");
          const available = await Promise.race([
            isBiometricAvailable(),
            new Promise<boolean>((r) => setTimeout(() => r(false), 5000)),
          ]);
          console.log("[BiometricOptIn] Biometric available:", available);
          if (!available) return;

          const typeLabel = await getBiometricTypeLabel();
          console.log("[BiometricOptIn] Type label:", typeLabel);
          setLabel(typeLabel);
          credsRef.current = creds;
          setOpen(true);
        } catch (e) {
          console.warn("[BiometricOptIn] Check failed:", e);
        }
      })();
    }, intervalMs);

    return () => clearInterval(poll);
  }, []);

  const handleEnable = async () => {
    const creds = credsRef.current;
    if (creds) {
      const saved = await saveCredentials(creds.email, creds.password);
      if (saved) {
        toast({
          title: `${label} Enabled`,
          description: `You can now use ${label} to log in next time.`,
        });
      }
    }
    credsRef.current = null;
    setOpen(false);
  };

  const handleSkip = () => {
    credsRef.current = null;
    setOpen(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Fingerprint className="h-5 w-5" />
            Enable {label}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            Would you like to use {label} to log in next time? Your credentials will be stored securely on this device.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleSkip}>No thanks</AlertDialogCancel>
          <AlertDialogAction onClick={handleEnable}>
            Enable {label}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
